import React, { useEffect, useContext, useState } from 'react';
import { useHistory, useLocation } from "react-router-dom";
import ReactPixel from "react-facebook-pixel";
import { AuthContext } from "../../context/Auth/AuthContext";
import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Divider
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ReceiptIcon from "@material-ui/icons/Receipt";
import { useDate } from "../../hooks/useDate";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(4),
    textAlign: "center",
    maxWidth: 600,
    borderRadius: 16,
  },
  icon: {
    fontSize: 80,
    color: "#4caf50",
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  message: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  button: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 4),
    fontSize: "1.1rem",
    borderRadius: 8,
  },
  infoBox: {
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(2),
    borderRadius: 8,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: "left",
  },
  infoItem: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  label: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  value: {
    fontWeight: 500,
  },
  totalBox: {
    backgroundColor: "#e8f5e9",
    padding: theme.spacing(2),
    borderRadius: 8,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: "2px solid #4caf50",
  },
  totalValue: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#2e7d32",
  },
  receiptIcon: {
    fontSize: 40,
    color: "#4caf50",
    marginBottom: theme.spacing(1),
  },
}));

function PagamentoSucesso() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const { dateToClient } = useDate();
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    // Recuperar dados do pagamento da location state ou buscar da API
    const fetchPaymentData = async () => {
      try {
        if (location.state?.paymentData) {
          setPaymentData(location.state.paymentData);
        } else {
          // Buscar dados da última fatura se não vierem na state
          const { data } = await api.get("/invoices/latest");
          setPaymentData(data);
        }
      } catch (error) {
        console.error("Erro ao buscar dados do pagamento:", error);
      }
    };

    fetchPaymentData();
  }, [location]);

  useEffect(() => {
    if (paymentData) {
      // Facebook Pixel: ViewContent quando página é carregada
      ReactPixel.track('ViewContent', {
        content_name: 'Pagamento Confirmado - Assinatura Ativa',
        content_category: 'payment_confirmation',
        content_ids: [String(user?.companyId)],
        content_type: 'subscription_payment',
        value: paymentData.value || 0,
        currency: 'BRL',
        user_email: user?.email
      });

      // Google Ads: Conversion event para purchase
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-18067457586/pHhSCLnLiZccELKMnqdD',
          'value': paymentData.value || 0,
          'currency': 'BRL',
          'transaction_id': paymentData.id || user?.companyId
        });
      }
    }
  }, [paymentData, user]);

  const handleGoToDashboard = () => {
    history.push("/");
  };

  if (!user || !paymentData) {
    return (
      <Container className={classes.container}>
        <CircularProgress style={{ color: "white" }} />
      </Container>
    );
  }

  const valorFormatado = (paymentData.value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });

  return (
    <Container className={classes.container}>
      <Paper className={classes.paper} elevation={3}>
        <CheckCircleIcon className={classes.icon} />

        <Typography variant="h4" className={classes.title}>
          Pagamento Confirmado! 💰
        </Typography>

        <Typography variant="h6" className={classes.message}>
          Sua assinatura está ativa!
        </Typography>

        <Typography variant="body1" style={{ marginBottom: 16 }}>
          Obrigado por escolher o ConexChat. Seu pagamento foi processado
          com sucesso via PIX.
        </Typography>

        <Divider style={{ margin: "16px 0" }} />

        <ReceiptIcon className={classes.receiptIcon} />
        <Typography variant="h6" style={{ marginBottom: 8 }}>
          Comprovante de Pagamento
        </Typography>

        <Box className={classes.totalBox}>
          <Typography variant="body2" style={{ marginBottom: 4 }}>
            Valor Total Pago
          </Typography>
          <Typography className={classes.totalValue}>
            {valorFormatado}
          </Typography>
        </Box>

        <Box className={classes.infoBox}>
          <div className={classes.infoItem}>
            <span className={classes.label}>Empresa:</span>
            <span className={classes.value}>{user.name || user.company?.name}</span>
          </div>
          <div className={classes.infoItem}>
            <span className={classes.label}>Email:</span>
            <span className={classes.value}>{user.email}</span>
          </div>
          <div className={classes.infoItem}>
            <span className={classes.label}>Método:</span>
            <span className={classes.value}>PIX</span>
          </div>
          <div className={classes.infoItem}>
            <span className={classes.label}>Transação:</span>
            <span className={classes.value}>
              {paymentData.id || user.companyId}
            </span>
          </div>
          {paymentData.dueDate && (
            <div className={classes.infoItem}>
              <span className={classes.label}>Próximo vencimento:</span>
              <span className={classes.value}>
                {dateToClient(paymentData.dueDate)}
              </span>
            </div>
          )}
        </Box>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          className={classes.button}
          onClick={handleGoToDashboard}
        >
          Ir para o Dashboard
        </Button>

        <Typography variant="caption" style={{ display: "block", marginTop: 16 }}>
          Um email de confirmação foi enviado para {user.email}
        </Typography>
      </Paper>
    </Container>
  );
}

export default PagamentoSucesso;